import React, { useEffect, useContext } from 'react';
import { Box, Typography, Button } from '@mui/material';
import AccessInfoIcon from "../../../assets/images/DQProImages/assess-info-icon.png";
import { useHistory } from "react-router-dom";
import { AuthContext } from '../../../context/auth';
import { getUserDetailsService, updateUserDetailsService } from '../../../services/auth.service';
import { planHasSequentialSteps, isClientOnboardingUser, mergeOnboardingRefreshIntoAuthUser } from '../DQProV2/constants/onboardingSteps.config';
import { ASSESSMENT_TYPE, USER_GROUPS } from "../../../utils/constants";
import { COS_PREVIEW_PATH } from "../../../utils/cpci.util";

// Driven by the workspace branding (falls back to the DQ orange).
const ORANGE = 'var(--brand-primary, #FF6900)';

// The CPC Index assessment is fixed to the "YoungPeople" audience — no audience picker.
const CPCI_ITEM = {
    assessment_type: ASSESSMENT_TYPE.CPCI,
    user_group: USER_GROUPS.YOUNGPEOPLE,
};

const cpciAssessmentSections = [
    {
        heading: 'What It Includes',
        items: [
            '179 questions across 7 sections, about 40 to 50 minutes',
            'Delivered across all your schools, with results shown on one dashboard for the whole network',
        ],
    },
    {
        heading: 'What It Does',
        items: [
            "Measures your children's readiness for online safety, from everyday habits to safety skills",
            'Measures their exposure to online risks like cyberbullying, unwanted contact, and harmful content',
            'Reads the context and support around them, at home and at school',
        ],
    },
    {
        heading: 'What It Delivers',
        items: [
            "A network dashboard of children's scorecards across every school",
            'Comparison against national and global benchmarks',
            'Priority areas that need the most support',
        ],
    },
];

/* ─── section block (same style as the Create page) ─── */
const SectionBlock = ({ heading, items }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--brand-text, #111827)', mb: 1 }}>
            {heading}
        </Typography>
        {items.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.2, mb: 0.75 }}>
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: ORANGE,
                        flexShrink: 0,
                        mt: '7px',
                    }}
                />
                <Typography sx={{ fontSize: '14px', color: 'var(--brand-text, #374151)', lineHeight: 1.6 }}>
                    {item}
                </Typography>
            </Box>
        ))}
    </Box>
);

/**
 * "Run a CPC Index Assessment" — the Step 1 landing page for enterprise_p0's
 * getting-started flow. Reuses (or creates, on first visit) the CPC Index
 * assessment for the fixed audience, then goes straight to the questions preview
 * — no audience picker, no separate name/confirm page.
 */
const DQProCpciAssessmentSuite = () => {
    const history = useHistory();
    const { authUser, setAuthUser } = useContext(AuthContext);

    // Onboarding: visiting this page (the Step 1 "Preview Assessments" card)
    // marks Step 1 complete so Step 2 unlocks — same on-view pattern as the
    // Step 3 dashboards. The preview→Exit write remains as a fallback.
    useEffect(() => {
        // Two audiences reach this page. planHasSequentialSteps covers the OWNER
        // step sets; a level-22 school runs on CLIENT_ONBOARDING_STEPS_BY_PLAN,
        // which the owner-only check never matches — so a P1 school completed
        // this step but it was never recorded, leaving Step 2 permanently locked
        // (PBDQ: "Step 2 not unlocked even after class and student setup").
        // Steps 2 and 3 already gate this way (ManageClasses, SchoolDashboard).
        if (!planHasSequentialSteps(authUser?.dq_pro_plan?.plan_key)
            && !isClientOnboardingUser(authUser)) return;
        updateUserDetailsService({ complete_onboarding_step: 1 })
            .then(() => getUserDetailsService())
            .then(res => {
                const refreshedUser = res?.data?.user || res?.data;
                // Merge ONLY onboarding progress — a full replace drops
                // login-time session fields (see mergeOnboardingRefreshIntoAuthUser).
                const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                if (merged !== authUser) setAuthUser(merged);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Preview is read-only: open the COS catalogue preview directly. Nothing is
    // built, so no credits/tokens are needed (works on an empty balance too).
    const handlePreview = () => {
        history.push({
            pathname: COS_PREVIEW_PATH,
            state: { fromOnboarding: true },
        });
    };

    return (
        <>
            <Box
                sx={{
                    px: { xs: 2, sm: 4, md: 0 },
                    py: { xs: 3, sm: 5 },
                    maxWidth: '1200px',
                    mx: 'auto',
                }}
            >
                {/* Page heading */}
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: ORANGE,
                        fontSize: { xs: '28px', sm: '36px' },
                        fontWeight: 700,
                        mb: 1,
                    }}
                >
                    Child Online Safety Assessments
                </Typography>
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: 'var(--brand-text, #111827)',
                        fontSize: { xs: '16px', sm: '18px' },
                        mb: 4,
                    }}
                >
                    Select an assessment below to preview.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {/* Card — same styles as the Create page's CreateCard */}
                <Box
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        maxWidth: { md: 620 },
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '20px',
                        boxShadow: '0px 4px 16px rgba(0,0,0,0.06)',
                        backgroundColor: 'var(--brand-surface, #ffffff)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Card header strip */}
                    <Box
                        sx={{
                            backgroundColor: '#0097B2',
                            px: 3,
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                            Children Assessment
                        </Typography>
                    </Box>

                    {/* Card body */}
                    <Box sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
                        {/* Icon + Title row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '16px',
                                    backgroundColor: 'var(--brand-surface, #FFF5EE)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <img src={AccessInfoIcon} alt="Measure Your Children's Online Safety" style={{ width: 52, height: 52, objectFit: 'contain' }} />
                            </Box>
                            <Typography sx={{ fontSize: { xs: '18px', sm: '22px' }, fontWeight: 700, color: 'var(--brand-text, #111827)', lineHeight: 1.3 }}>
                                Measure Your Children's Online Safety
                            </Typography>
                        </Box>

                        {/* Section blocks */}
                        <Box sx={{ flexGrow: 1, mb: 1.5 }}>
                            {cpciAssessmentSections.map((s, i) => (
                                <SectionBlock key={i} heading={s.heading} items={s.items} />
                            ))}
                        </Box>

                        {/* CTA */}
                        <Button
                            onClick={() => handlePreview(CPCI_ITEM)}
                            sx={{
                                alignSelf: 'flex-start',
                                backgroundColor: ORANGE,
                                color: '#fff',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '15px',
                                px: 4,
                                py: 1.25,
                                borderRadius: '10px',
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            }}
                        >
                            Preview Assessment
                        </Button>
                    </Box>
                </Box>
                </Box>
            </Box>
        </>
    );
};

export default DQProCpciAssessmentSuite;
